import React from 'react';
import { Box, Text } from '@nimbus-ds/components';
import {
  CreditCardIcon,
  MoneyIcon,
  QrCodeIcon,
  TransferPesoIcon,
} from '@nimbus-ds/icons';
import { StoreFlag } from 'commons/enums';
import { useStoreFlag } from 'commons/hooks/useStoreFlag';
import { DiscountType, PaymentMethodsType } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import { PaymentMethodsSkeleton } from '../shared/components';
import BankTransferContent from './BankTransfer/BankTransferContent';
import CardContent from './Card/CardContent';
import PaymentMethodCard from './PaymentMethodCard';

interface PaymentMethodsArgentinaProps {
  discounts: DiscountType[];
  setDiscounts: React.Dispatch<React.SetStateAction<DiscountType[]>>;
  setDiscountChanged: React.Dispatch<React.SetStateAction<boolean>>;
  paymentMethods: Array<{
    enabled: boolean;
    paymentMethod: PaymentMethodsType;
  }>;
  onCheckPaymentMethod: (
    enabled: boolean,
    paymentMethod: PaymentMethodsType,
  ) => void;
  showNoActiveError: boolean;
  isPaymentMethodEnabled: (method: PaymentMethodsType) => boolean;
  hasActivePromotions: boolean;
}

function PaymentMethodsArgentina({
  discounts,
  setDiscounts,
  setDiscountChanged,
  paymentMethods,
  onCheckPaymentMethod,
  showNoActiveError,
  isPaymentMethodEnabled,
  hasActivePromotions,
}: PaymentMethodsArgentinaProps): JSX.Element {
  const { t } = useTranslationWithPrefix('paymentMethods');
  const { enabled: hasSubscriptions } = useStoreFlag(
    StoreFlag.SubscriptionsRecurrenceActive,
  );

  const isWalletAvailable = paymentMethods?.some(
    (method) => method.paymentMethod === PaymentMethodsType.Wallet,
  );

  const getIconForPaymentMethod = (method: PaymentMethodsType) => {
    switch (method) {
      case 'credit_card':
      case 'debit_card':
        return <CreditCardIcon />;

      case 'ticket':
        return <MoneyIcon />;

      case 'wire_transfer':
        return <TransferPesoIcon />;

      case 'wallet':
        return <QrCodeIcon />;

      default:
        return;
    }
  };

  const getPaymentMethodContent = (method: PaymentMethodsType) => {
    switch (method) {
      case 'wire_transfer':
        return (
          <BankTransferContent
            discounts={discounts}
            setDiscounts={setDiscounts}
            hasActivePromotions={hasActivePromotions}
            setDiscountChanged={setDiscountChanged}
            enabled={isPaymentMethodEnabled(PaymentMethodsType.WireTransfer)}
          />
        );
      case 'credit_card':
        if (!isWalletAvailable) {
          return;
        }
        return (
          <CardContent
            getIconForPaymentMethod={getIconForPaymentMethod}
            checkPaymentMethod={onCheckPaymentMethod}
            isCardEnabled={isPaymentMethodEnabled(
              PaymentMethodsType.CreditCard,
            )}
            isWalletEnabled={isPaymentMethodEnabled(PaymentMethodsType.Wallet)}
          />
        );
      default:
        return;
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="2">
      {paymentMethods && paymentMethods.length > 0 && (
        <Box display="flex" flexDirection="column" gap="4">
          {paymentMethods.map((method) => {
            if (method.paymentMethod === PaymentMethodsType.Wallet) {
              // Wallet will be inside the card option
              return null;
            }
            const isCreditCard =
              method.paymentMethod === PaymentMethodsType.CreditCard;
            const disabled = isCreditCard && hasSubscriptions;

            return (
              <Box key={method.paymentMethod}>
                <PaymentMethodCard
                  method={method}
                  onCheckPaymentMethod={onCheckPaymentMethod}
                  getPaymentMethodContent={getPaymentMethodContent}
                  disabled={disabled}
                />
              </Box>
            );
          })}
        </Box>
      )}
      {showNoActiveError && (
        <Text color="danger-interactive">{t('noActiveDescription')}</Text>
      )}
    </Box>
  );
}

PaymentMethodsArgentina.Skeleton = PaymentMethodsSkeleton;
export default PaymentMethodsArgentina;
